import React, { useState } from 'react';
import { CalendarClock, MessageCircle, Phone, SquarePen } from 'lucide-react';
import StatusBadge from './StatusBadge';
import StatusActions from './StatusActions';
import { getAppointmentTimingState, getAppointmentUiState, getEditableStatuses } from '../utils/appointmentStatus';

function formatPrice(value) {
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export default function AppointmentCard({
  appointment,
  timeLabel,
  onCall,
  onSms,
  onReschedule,
  onSetStatus,
}) {
  const [showStatusEditor, setShowStatusEditor] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const uiState = getAppointmentUiState(appointment);
  const timingState = getAppointmentTimingState(appointment);
  const showReschedule = Boolean(onReschedule) && appointment.status === 'scheduled' && timingState === 'before';
  const editableStatuses = Boolean(onSetStatus) ? getEditableStatuses(appointment) : [];
  const canEditStatus = editableStatuses.length > 0;
  const addonSummary = Array.isArray(appointment.selected_addons) && appointment.selected_addons.length > 0
    ? appointment.selected_addons.map((addon) => addon.name).join(', ')
    : '';

  return (
    <div className="appt-card">
      <div className="appt-time">{timeLabel}</div>
      <div className="appt-info">
        <div className="appt-client-row">
          <div className="appt-client">{appointment.client_name || 'Klientka'}</div>
        </div>
        <div className="appt-service">
          {appointment.service_name} · {appointment.duration_minutes} min · {formatPrice(appointment.price)} zł
        </div>
        {addonSummary && <div className="appt-addons">Dodatki: {addonSummary}</div>}
        {appointment.notes && <div className="appt-notes">{appointment.notes}</div>}

        <div className="appt-status-block">
          <StatusBadge state={uiState} />
          {canEditStatus && (
            <button
              type="button"
              className="status-edit-btn"
              disabled={isUpdatingStatus}
              aria-expanded={showStatusEditor}
              onClick={() => setShowStatusEditor((current) => !current)}
            >
              <SquarePen size={14} strokeWidth={1.9} />
              {showStatusEditor ? 'Zamknij' : 'Zmień status'}
            </button>
          )}
          {showStatusEditor && (
            <StatusActions
              options={editableStatuses}
              disabled={isUpdatingStatus}
              onConfirm={async (status) => {
                try {
                  setIsUpdatingStatus(true);
                  await onSetStatus(status);
                  setShowStatusEditor(false);
                } finally {
                  setIsUpdatingStatus(false);
                }
              }}
            />
          )}
        </div>
      </div>
      <div className="appt-actions">
        <button type="button" className="icon-btn appt-action-btn" title="Zadzwoń" aria-label="Zadzwoń" onClick={onCall}>
          <Phone size={18} strokeWidth={1.9} />
          <span className="appt-action-label">Zadzwoń</span>
        </button>
        <button type="button" className="icon-btn appt-action-btn" title="SMS" aria-label="Wyślij SMS" onClick={onSms}>
          <MessageCircle size={18} strokeWidth={1.9} />
          <span className="appt-action-label">SMS</span>
        </button>
        {showReschedule && (
          <button type="button" className="icon-btn appt-action-btn" title="Przenieś termin" aria-label="Przenieś termin" onClick={onReschedule}>
            <CalendarClock size={18} strokeWidth={1.9} />
            <span className="appt-action-label">Przenieś</span>
          </button>
        )}
      </div>
    </div>
  );
}